import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Context } from "../../../context/Context";
import { Button, Input, NoticeBox, Upload, XCircle } from "../../ui";
import { ConfirmModal } from "../../modals/Confirm";
import RejectModal from "../../modals/RejectModal";
import DashboardModal from "../../modals/DashboardModal";
import Applications from "../Applications/Applications";
import Accounts from "../Accounts/Accounts";
import Campaigns from "../Campaigns/Campaigns";
import Branches from "../Branches/Branches";
import Performance, { hasLudPerformance } from "../Performance/Performance";
import Requests from "../Requests/Requests";
import Completion from "../Completion/Completion";
import { DASHBOARD_TEXT } from "../data/dashboardText";
import useDashboard from "../hooks/useDashboard";
import { SummaryCard, RestaurantsOverview } from "./DashboardBase";
import BrandAndQuickCards from "./BrandAndQuickCards";
import CashierView from "./CashierView";
import AllClientsActionsView from "./AllClientsActionsView";
import "./LudDashboard.css";
import DashboardAnalytics from "../dashboardAnalytics/DashboardAnalytics";
import useOnboardingDraft from "../hooks/useOnboardingDraft";
import useBranchPreparation from "../hooks/useBranchPreparation";
import { resolveDashboardMode, useAccess } from "../../../access";
import AddStoreModal from "./AddStoreModal";
import { createMyStoreAPI } from "../../../utils/api/stores";
import { clearReportCache } from "../../../utils/useReportQuery";

// One dashboard for every client: a `new` client (a store still being set up, no active subscription yet) sees the same layout, sidebar and
// sections as a `store` / `storeL`. What differs comes from the access layer, never from a separate screen: the permissions the role holds
// (src/access) and the store's subscription (useOperationalAccess), which lock the operational actions and say why.
// The accountant (fa) works in Billing: this route sends it there before any dashboard data is requested.
export default function LudDashboard() {
  const { can } = useAccess();
  if (resolveDashboardMode(can) === "accountant") return <Navigate to="/auth/billing" replace />;
  return <LudDashboardContent />;
}

function LudDashboardContent() {
  const context = useContext(Context);
  const { can } = useAccess();
  const lang = context?.lang === "en" ? "en" : "ar";
  const text = DASHBOARD_TEXT[lang];
  // Arriving from the Menu ("Save as Draft"): open the SAME restaurant and highlight the new request(s).
  const arrival = useLocation().state || {};
  const dashboard = useDashboard({ context, initialStoreId: typeof arrival.storeId === "string" ? arrival.storeId : undefined });
  const navigate = useNavigate();
  // Adding a store to the account (store / storeL only): the form, the "store added" notice and the jump to Completion that follows.
  const [addStoreOpen, setAddStoreOpen] = useState(false);
  const addedStoreId = typeof arrival.storeAdded === "string" ? arrival.storeAdded : "";
  const [addedNotice, setAddedNotice] = useState(Boolean(addedStoreId));
  const canAddStore = can("restaurant.createOwn");
  const handleCreateStore = async (form) => {
    const result = await createMyStoreAPI(localStorage.getItem("auth-token"), form);
    const newId = result.store?.id;
    setAddStoreOpen(false);
    // Nothing loaded for the previously selected store may outlive the switch: cached report rows go now, and the session refresh below
    // (new `between` -> new access signature) remounts the app tree, which drops every store-scoped state. The router state names the store
    // to open, so the remounted dashboard lands on the NEW store, not on the first one in the list.
    clearReportCache();
    setAddedNotice(true);
    navigate("/auth/lud-dashboard", { replace: true, state: { storeId: newId, storeAdded: newId } });
    await context?.retrySession?.();
    return result;
  };
  // Right after adding: the new store is selected and its data loaded -> take the customer to Completion once.
  const scrolledFor = useRef("");
  useEffect(() => {
    if (!addedStoreId || scrolledFor.current === addedStoreId) return;
    if (dashboard.selectedStoreId !== addedStoreId || dashboard.loading) return;
    scrolledFor.current = addedStoreId;
    document.getElementById("completion")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [addedStoreId, dashboard.selectedStoreId, dashboard.loading]);
  const highlightIds = Array.isArray(arrival.highlightRequestIds) ? arrival.highlightRequestIds.map(String) : [];
  const selectedRestaurantId = dashboard.selectedStoreId;
  const setSelectedRestaurantId = dashboard.setSelectedStoreId;
  const [confirmRequestId, setConfirmRequestId] = useState(null);
  const [rejectRequestId, setRejectRequestId] = useState(null);
  const [completionHasMissing, setCompletionHasMissing] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  // Sections that decided they have nothing useful to show (an inactive subscription and no history): the wrapper is hidden too.
  const [appsHidden, setAppsHidden] = useState(false);
  const [requestsHidden, setRequestsHidden] = useState(false);
  const bannerInputRef = React.useRef(null);
  const [pricing, setPricing] = useState({
    lBase: 400,
    lExtra: 50,
    uBase: 600,
    uExtra: 200,
    dBase: 1400,
    dExtra: 500,
    integration: 600,
    customItems: [],
  });
  React.useEffect(() => {
    if (dashboard.pricing) setPricing(dashboard.pricing);
  }, [dashboard.pricing]);
  const selectedRestaurant = dashboard.stores.find(
    (item) => item.id === selectedRestaurantId,
  ) ||
    dashboard.stores[0] || {
      id: selectedRestaurantId || "",
      nameAr: "",
      nameEn: "",
      package: "",
      branches: 0,
    };
  // A client whose store is still being set up writes its profile, documents and DRAFT branches through the onboarding draft API (which
  // honours the information-review lock). Everyone who may edit through the dashboard completion endpoint keeps doing so.
  const storeScope = { restaurantId: selectedRestaurant.id };
  const draftEnabled =
    Boolean(selectedRestaurant.id) &&
    can("onboarding.draft.edit", storeScope) &&
    !can("ludDashboard.completion.edit");
  const draft = useOnboardingDraft({
    storeId: selectedRestaurant.id,
    enabled: draftEnabled,
    lang,
    onChanged: dashboard.refresh,
  });
  // Branch preparation (draft branches, working hours, license, branch users): every client owner, no subscription needed.
  const prep = useBranchPreparation({
    storeId: selectedRestaurant.id,
    enabled: Boolean(selectedRestaurant.id) && can("restaurant.branch.prepare", storeScope),
    onChanged: dashboard.refresh,
  });
  // May THIS branch be activated (covered today)? From the subscription summary; the same rules as the entitlement.
  const branchCoverage = useMemo(() => {
    const list = dashboard.selectedSubscription?.summary?.branchCoverage?.branches;
    return Array.isArray(list) ? Object.fromEntries(list.map((entry) => [entry.branchId, entry.covered === true])) : null;
  }, [dashboard.selectedSubscription]);
  const draftFiles = draftEnabled
    ? {
        save: async (files) => {
          const [key, file] = Object.entries(files || {})[0] || [];
          if (key && file) await draft.uploadDocument(key, file);
        },
        remove: (key) => draft.removeDocument(key),
        open: async (record) => {
          const url = await draft.documentUrl(record.key);
          window.open(url, "_blank", "noopener,noreferrer");
        },
        uploadBanner: (file) => draft.uploadDocument("banner", file),
        removeBanner: () => draft.removeDocument("banner"),
      }
    : null;
  // The subscription became active while the account is still `new`: ask the session for the promoted role (a few tries, the server
  // promotes the verified owner right after the payment is approved or the grant starts).
  const rolePending = dashboard.operational.rolePending;
  const sessionTries = useRef(0);
  useEffect(() => {
    if (!rolePending) {
      sessionTries.current = 0;
      return undefined;
    }
    if (typeof context?.retrySession !== "function" || sessionTries.current >= 3) return undefined;
    const timer = setTimeout(() => {
      sessionTries.current += 1;
      context.retrySession();
    }, sessionTries.current === 0 ? 0 : 8000);
    return () => clearTimeout(timer);
  }, [rolePending, context]);
  const showRequestsNearApps = dashboard.requests.some(
    (request) =>
      request.status === "pending" ||
      request.status === "awaitingContractReview",
  );
  const summary = useMemo(
    () => ({
      activeApps: dashboard.applications.filter(
        (item) => item.status === "active",
      ).length,
      pendingRequests: dashboard.requests.filter(
        (item) => item.status === "pending",
      ).length,
      branches: dashboard.branches.length,
    }),
    [dashboard.applications, dashboard.branches, dashboard.requests],
  );
  const handleReview = (id, decision) => {
    if (decision === "approved") setConfirmRequestId(id);
    if (decision === "rejected") setRejectRequestId(id);
  };
  if (dashboard.page === "cashier")
    return <CashierView lang={lang} dashboard={dashboard} />;
  if (dashboard.page === "actions")
    return (
      <AllClientsActionsView
        lang={lang}
        dashboard={dashboard}
        onReview={handleReview}
      />
    );
  return (
    <div className="lud-dashboard-shell" dir={lang === "ar" ? "rtl" : "ltr"}>
      <main className="lud-dashboard-page" id="dashboard">

        {dashboard.role === "admin" && (
          <header className="lud-dashboard-topbar">
            <div>
              <p>
                {lang === "ar"
                  ? `${selectedRestaurant.nameAr} · ${selectedRestaurant.id}`
                  : `${selectedRestaurant.nameEn} · ${selectedRestaurant.id}`}
              </p>
            </div>
            <div className="lud-dashboard-topbar-actions">
              <div className="lud-role-switch">
                <button
                  type="button"
                  className={dashboard.role === "admin" ? "active" : ""}
                  onClick={() => dashboard.setRole("admin")}
                >
                  {text.admin}
                </button>
                <button
                  type="button"
                  className={dashboard.role === "customer" ? "active" : ""}
                  onClick={() => dashboard.setRole("customer")}
                >
                  {text.customer}
                </button>
              </div>
              {can("billing.accountant.view") && (
                <Button
                  size="sm"
                  appearance="soft"
                  onClick={() => navigate("/auth/billing?tab=accountant")}
                >
                  {text.accountant}
                </Button>
              )}
              <Button
                size="sm"
                appearance="soft"
                onClick={() => dashboard.setPage("cashier")}
              >
                {text.cashier}
              </Button>
              {dashboard.role === "admin" && (
                <Button
                  size="sm"
                  appearance="soft"
                  onClick={() => dashboard.setPage("actions")}
                >
                  {text.actions}
                </Button>
              )}
            </div>
          </header>
        )}

        <RestaurantsOverview
          lang={lang}
          selectedId={selectedRestaurantId}
          onSelect={setSelectedRestaurantId}
          onOpenPricing={() => setPricingOpen(true)}
          dashboard={dashboard}
          isAdmin={dashboard.role === "admin"}
          restaurants={dashboard.stores}
          onAddStore={canAddStore ? () => setAddStoreOpen(true) : undefined}
        />

        {addedNotice && addedStoreId && dashboard.selectedStoreId === addedStoreId && (
          <div role="status">
            <NoticeBox variant="success" size="sm">
              {lang === "ar"
                ? "تم إنشاء المتجر الجديد بصيغة مسودة. أكمل بياناته من قسم اكتمال البيانات، ثم اشترك عندما تكون جاهزًا."
                : "The new store was created as a draft. Complete its details below, then subscribe when you are ready."}
            </NoticeBox>
          </div>
        )}
        {addStoreOpen && canAddStore && (
          <AddStoreModal
            lang={lang}
            defaults={{
              name: context?.currentUserInfo?.name,
              phone: context?.currentUserInfo?.phone,
              email: context?.currentUserInfo?.contactEmail,
            }}
            onClose={() => setAddStoreOpen(false)}
            onCreate={handleCreateStore}
          />
        )}

        {dashboard.role !== "customer" && (
          <div className="lud-dashboard-summary-grid">
            <SummaryCard
              label={lang === "ar" ? "التطبيقات الفعالة" : "Active Apps"}
              value={summary.activeApps}
            />
            <SummaryCard
              label={lang === "ar" ? "الطلبات المعلقة" : "Pending Requests"}
              value={summary.pendingRequests}
              tone="warning"
            />
            <SummaryCard label={text.branches} value={summary.branches} />
            <SummaryCard
              label={text.subscription.unpaidInvoices}
              value={
                dashboard.selectedSubscription.summary?.unpaidInvoiceCount ??
                "—"
              }
              tone="danger"
            />
          </div>
        )}

        <section id="apps" hidden={appsHidden}>
          <Applications
            onHiddenChange={setAppsHidden}
            lang={lang}
            storeId={selectedRestaurant.id}
            role={dashboard.role}
            onChanged={dashboard.refresh}
            onSelectStore={setSelectedRestaurantId}
            onGoToCompletion={() =>
              document
                .getElementById("completion")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          />
        </section>

        <div className="lud-dashboard-dynamic-flow">
          <BrandAndQuickCards
            lang={lang}
            dashboard={dashboard}
            restaurant={selectedRestaurant}
            subscription={dashboard.selectedSubscription}
          />
          <section
            className={`lud-app-ad-banner${dashboard.banner?.url ? " has-image" : ""}`}
          >
            {dashboard.banner?.url ? (
              <img src={dashboard.banner.url} alt="" />
            ) : (
              <div>
                <strong>
                  {lang === "ar"
                    ? "مساحة إعلانية للتطبيقات"
                    : "Applications advertising space"}
                </strong>
                <span>1200 × 250</span>
              </div>
            )}
            {dashboard.role === "admin" && (
              <div className="lud-app-ad-banner-actions">
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (file) await dashboard.uploadBanner(file);
                    event.target.value = "";
                  }}
                />
                {!dashboard.banner?.url && (
                  <Button
                    size="sm"
                    appearance="soft"
                    title={lang === "ar" ? "رفع البنر" : "Upload Banner"}
                    aria-label={lang === "ar" ? "رفع البنر" : "Upload Banner"}
                    onClick={() => bannerInputRef.current?.click()}
                  >
                    <Upload size={18} />
                  </Button>
                )}
                {dashboard.banner?.url && (
                  <Button
                    size="sm"
                    variant="danger"
                    appearance="soft"
                    title={lang === "ar" ? "إزالة البنر" : "Remove Banner"}
                    aria-label={lang === "ar" ? "إزالة البنر" : "Remove Banner"}
                    onClick={dashboard.removeBanner}
                  >
                    <span aria-hidden="true">
                      {" "}
                      <XCircle size={14} />
                    </span>
                  </Button>
                )}
              </div>
            )}
          </section>
          <Accounts
            lang={lang}
            text={text}
            role={dashboard.role}
            accounts={dashboard.accounts}
            managers={dashboard.managers}
            platformLinks={dashboard.platformLinks}
            onSaveAccount={dashboard.saveAccount}
            onSavePlatformLinks={dashboard.savePlatformLinks}
            onSaveManager={dashboard.saveManager}
            onDeleteAccount={dashboard.deleteAccount}
            onDeleteManager={dashboard.deleteManager}
            isAdmin={dashboard.role === "admin"}
          />
          <Campaigns
            lang={lang}
            text={text}
            campaigns={dashboard.campaigns}
            role={dashboard.role}
            onSaveCampaign={dashboard.addCampaign}
          />
          <div
            id="completion"
            className={
              completionHasMissing
                ? "lud-dashboard-dynamic-priority lud-dashboard-dynamic-priority-completion"
                : ""
            }
          >
            <Completion
              lang={lang}
              text={text}
              items={dashboard.completionItems}
              percent={dashboard.completionPercent}
              store={dashboard.store}
              organizationDocuments={dashboard.organizationDocuments}
              onComplete={draftEnabled ? (_item, payload) => draft.saveProfile(payload) : dashboard.completeItem}
              onSaveDocuments={draftFiles ? draftFiles.save : dashboard.saveOrganizationDocuments}
              onDeleteDocument={draftFiles ? draftFiles.remove : dashboard.deleteOrganizationDocument}
              banner={dashboard.banner}
              onUploadBanner={draftFiles ? draftFiles.uploadBanner : dashboard.uploadBanner}
              onRemoveBanner={draftFiles ? draftFiles.removeBanner : dashboard.removeBanner}
              canManageFiles={dashboard.role === "admin" || (draftEnabled && draft.canEdit)}
              readOnly={draftEnabled && !draft.canEdit}
              onOpenDocument={draftFiles ? draftFiles.open : dashboard.openOrganizationDocument}
              onMissingChange={setCompletionHasMissing}
              needsSubscription={dashboard.operational.operational === false}
            />
          </div>
          <div className="dashboard-performance-branches">
            {(dashboard.loading || hasLudPerformance(dashboard.applications)) && (
            <section id="performance">
              <Performance
                lang={lang}
                text={text}
                campaigns={dashboard.campaigns}
                applications={dashboard.applications}
                store={dashboard.store}
                menu={
                  dashboard.menuSummary?.available
                    ? {
                        itemCount: dashboard.menuSummary.itemsCount,
                        boxCount: dashboard.menuSummary.boxesCount,
                      }
                    : undefined
                }
                loading={dashboard.loading}
              />
            </section>
            )}
            <section id="branches">
              <Branches
                lang={lang}
                text={text}
                role={dashboard.role}
                storeId={selectedRestaurant.id}
                prep={prep}
                coverage={branchCoverage}
                lock={dashboard.requestLock}
                onChanged={dashboard.refresh}
                onGoToCompletion={() =>
                  document
                    .getElementById("completion")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                branches={dashboard.branches}
                onAddBranch={dashboard.addBranch}
                onSaveHours={dashboard.saveBranchHours}
                onUpdateBranch={dashboard.updateBranch}
                onUpdateBranchApps={dashboard.updateBranchApps}
                onUpdateBranchStatus={dashboard.updateBranchStatus}
                onUpdateBranchIntegrationStatus={
                  dashboard.updateBranchIntegrationStatus
                }
                onAddBranchUser={dashboard.addBranchUser}
                onDeleteBranch={dashboard.deleteBranch}
                onUploadBranchLicense={dashboard.uploadBranchLicense}
                onRemoveBranchLicense={dashboard.removeBranchLicense}
                onGetBranchLicenseUrl={dashboard.getBranchLicenseUrl}
              />
            </section>
          </div>
          <section id="analysis">
            <DashboardAnalytics storeIds={[selectedRestaurant.id]} />
          </section>
          <section
            id="app-requests"
            hidden={requestsHidden}
            className={
              showRequestsNearApps
                ? "lud-dashboard-dynamic-priority lud-dashboard-dynamic-priority-requests"
                : ""
            }
          >
            <Requests
              onHiddenChange={setRequestsHidden}
              lang={lang}
              text={text}
              role={dashboard.role}
              lock={dashboard.requestLock}
              requests={dashboard.requests}
              onReview={handleReview}
              onUploadContract={dashboard.uploadContractForCustomer}
              onUploadSignedContract={dashboard.uploadSignedContract}
              onReviewSignedContract={dashboard.reviewSignedContract}
              onOpenRequestFile={dashboard.openRequestFile}
              onUploadRequestFile={dashboard.uploadRequestAttachment}
              onCreateRequest={dashboard.createRequest}
              branches={dashboard.branches}
              applications={dashboard.applications}
              stores={dashboard.stores}
              selectedStoreId={dashboard.selectedStoreId}
              highlightIds={highlightIds}
              accounts={dashboard.accounts}
              pricing={dashboard.pricing}
              onChanged={dashboard.refresh}
            />
          </section>
        </div>
      </main>

      <DashboardModal
        open={pricingOpen}
        title={
          lang === "ar"
            ? "إدارة الأسعار والبنود"
            : "Pricing and Items Management"
        }
        onClose={() => setPricingOpen(false)}
        size="lg"
        footer={
          <>
            <Button
              variant="primary"
              appearance="filled"
              onClick={() => {
                dashboard.updatePricing(pricing);
                setPricingOpen(false);
              }}
            >
              {text.save}
            </Button>
            <Button
              variant="danger"
              appearance="soft"
              onClick={() => setPricingOpen(false)}
            >
              {text.cancel}
            </Button>
          </>
        }
      >
        <p className="lud-modal-description">
          {lang === "ar"
            ? "أي تعديل هنا ينعكس فورًا على إعدادات الأسعار الجديدة لكل المطاعم."
            : "Changes here apply to new pricing settings for all restaurants."}
        </p>
        <div className="lud-pricing-form">
          {[
            ["L", "lBase", "lExtra"],
            ["U", "uBase", "uExtra"],
            ["D", "dBase", "dExtra"],
          ].map(([pkg, baseKey, extraKey]) => (
            <div className="lud-pricing-row" key={pkg}>
              <strong>
                {lang === "ar"
                  ? `باقة ${pkg} — السعر الأساسي / فرع إضافي (ر.س)`
                  : `Package ${pkg} — base / extra branch (SAR)`}
              </strong>
              <Input
                type="number"
                value={pricing[baseKey]}
                onChange={(e) =>
                  setPricing((p) => ({
                    ...p,
                    [baseKey]: Number(e.target.value),
                  }))
                }
              />
              <Input
                type="number"
                value={pricing[extraKey]}
                onChange={(e) =>
                  setPricing((p) => ({
                    ...p,
                    [extraKey]: Number(e.target.value),
                  }))
                }
              />
            </div>
          ))}
          <Input
            type="number"
            label={
              lang === "ar"
                ? "التكامل — السعر لكل فرع (ر.س)"
                : "Integration — price per branch (SAR)"
            }
            value={pricing.integration}
            onChange={(e) =>
              setPricing((p) => ({ ...p, integration: Number(e.target.value) }))
            }
          />
          <div className="lud-custom-items-box">
            <strong>
              {lang === "ar"
                ? "بنود إضافية (تظهر كخيار في كل فاتورة)"
                : "Additional items (available on every invoice)"}
            </strong>
            <div className="lud-pricing-row">
              <Input placeholder={lang === "ar" ? "اسم البند" : "Item name"} />
              <Input
                type="number"
                placeholder={lang === "ar" ? "السعر" : "Price"}
              />
              <Button size="sm" appearance="soft">
                {lang === "ar" ? "+ إضافة" : "+ Add"}
              </Button>
            </div>
          </div>
        </div>
      </DashboardModal>
      {confirmRequestId && (
        <ConfirmModal
          title={lang === "ar" ? "تأكيد الموافقة" : "Confirm Approval"}
          message={
            lang === "ar"
              ? "هل أنت متأكد من الموافقة على هذا الطلب؟"
              : "Are you sure you want to approve this request?"
          }
          onConfirm={() => {
            dashboard.reviewRequest(confirmRequestId, "approved");
            setConfirmRequestId(null);
          }}
          onCancel={() => setConfirmRequestId(null)}
        />
      )}
      <RejectModal
        open={Boolean(rejectRequestId)}
        onClose={() => setRejectRequestId(null)}
        onConfirm={(reason) => {
          dashboard.reviewRequest(rejectRequestId, "rejected", reason);
          setRejectRequestId(null);
        }}
      />
    </div>
  );
}
